/**
 * Multi-line chat input with suggestion chips and deep review toggle.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, Loader2 } from 'lucide-react';

const SUGGESTION_CHIPS = [
  "What's my health score?",
  "Show margin guard alerts",
  "Top products by revenue",
  "What are my active goals?",
  "Geographic order breakdown",
  "SEO audit results",
  "Recommend next actions",
];

export default function ChatInput({ onSend, isStreaming, showSuggestions }) {
  const [text, setText] = useState('');
  const [deepReview, setDeepReview] = useState(false);
  const textareaRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 150) + 'px';
    }
  }, [text]);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;
    onSend(trimmed, deepReview ? 'deep_review' : 'default');
    setText('');
    setDeepReview(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-white/10 bg-black/20 p-3">
      {/* Suggestion chips */}
      {showSuggestions && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {SUGGESTION_CHIPS.map((chip) => (
            <button
              key={chip}
              onClick={() => onSend(chip, 'default')}
              disabled={isStreaming}
              className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-white/60 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-50"
            >
              {chip}
            </button>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isStreaming ? 'Waiting for response...' : 'Ask about your business...'}
            disabled={isStreaming}
            rows={1}
            className="w-full resize-none bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#3EF1C7]/50 disabled:opacity-50"
          />
        </div>

        {/* Deep review toggle */}
        <button
          onClick={() => setDeepReview(!deepReview)}
          title={deepReview ? 'Deep review mode (Opus 4.6)' : 'Standard mode (Sonnet)'}
          className={`p-2.5 rounded-xl border transition-colors ${
            deepReview
              ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
              : 'bg-white/5 border-white/10 text-white/40 hover:text-white/60'
          }`}
        >
          <Sparkles className="w-4 h-4" />
        </button>

        {/* Send button */}
        <button
          onClick={handleSubmit}
          disabled={!text.trim() || isStreaming}
          className="p-2.5 rounded-xl bg-[#3EF1C7] text-black hover:bg-[#3EF1C7]/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          {isStreaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>

      {/* Mode indicator */}
      {deepReview && (
        <div className="mt-1.5 text-xs text-amber-400/60 flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          Deep Review mode — Opus 4.6 with full business context
        </div>
      )}
    </div>
  );
}
